/**
 * Tracks material changes without exclusive ownership of dirty flags.
 *
 * Each consumer that needs to react to material changes creates its own
 * MaterialChangeTracker instance. It compares a material's _changeCount
 * against the last seen value, so multiple consumers can independently
 * detect changes without resetting shared flags.
 *
 * Uses a WeakMap so that disposed materials are automatically cleaned up
 * by the garbage collector without requiring explicit dispose listeners.
 */
export class MaterialChangeTracker {

  constructor() {
    /** @type {WeakMap<THREE.Material, number>} material -> last seen _changeCount */
    this._seen = new WeakMap();

    // Fast path: cache the last checked material to avoid WeakMap lookup
    // when the same material is queried repeatedly.
    this._lastMaterial = null;
    this._lastCount = -1;
  }

  /**
   * Returns true if the material has changed since the last call
   * for the same material from this tracker instance.
   * @param {THREE.Material} material
   * @returns {boolean}
   */
  materialChanged(material) {
    const count = material._changeCount ?? 0;

    // Fast path: same material as last call
    if (material === this._lastMaterial) {
      if (count === this._lastCount) {
        return false;
      }
      this._lastCount = count;
      this._seen.set(material, count);
      return true;
    }

    // Slow path: different material – check the map
    this._lastMaterial = material;
    this._lastCount = count;

    const prev = this._seen.get(material);
    if (prev === count) {
      return false;
    }

    this._seen.set(material, count);
    return true;
  }

  /**
   * Resets tracking state for all materials.
   */
  clear() {
    this._seen = new WeakMap();
    this._lastMaterial = null;
    this._lastCount = -1;
  }
}